import { IconButton } from "@mui/material";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import useEvent from "react-use-event-hook";

import { PIC } from "@ryde/assets";
import {
  FilterDropdownWrapper,
  FilterSelectControl,
  FramerTooltip,
  selectStyles,
  selectStylesMobile,
} from "@ryde/controls";
import { getGridLabelKey, IDropdownItem, useIsMobile } from "@ryde/shared";

import { REPORT_FILTER_ENTITY } from "../../dynamic-dashboard.constants";
import { DashboardCardFilters } from "../dashboard-card-filters";
import { ICardEditorFiltersProps } from "./dashboard-card-editor.models";

/**
 * The card's own filters inside the editor. Options come from the selected
 * report's `availableFilters`, so the panel only ever offers filters that report
 * understands — unlike the dashboard-wide bar, which offers the union across
 * every analytic report. Values are held in the editor's draft state and written
 * onto the card by Save.
 */
export const CardEditorFilters: FC<ICardEditorFiltersProps> = ({
  schema,
  values,
  singleValueFilterKeys,
  addCardFilter,
  setCardFilterValues,
  removeCardFilter,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const [isAddOpen, setIsAddOpen] = useState(false);

  const addableOptions = useMemo(
    () =>
      Object.keys(schema)
        .filter(key => !(key in values))
        .map(key => ({
          value: key,
          label: t(getGridLabelKey(REPORT_FILTER_ENTITY, key)),
        })) as IDropdownItem[],
    [schema, values, t],
  );

  const stylesComputed = useMemo(
    () => (isMobile ? selectStylesMobile : selectStyles),
    [isMobile],
  );

  const handleToggleAdd = useEvent(() => setIsAddOpen(prev => !prev));
  const handleCloseAdd = useEvent(() => setIsAddOpen(false));

  // The dropdown below is `isMulti`, so react-select hands back an array and only
  // the first pick matters (adding one closes the menu). Reading a lone option too
  // costs nothing and means dropping `isMulti` later can't quietly turn Add into a
  // no-op.
  const onAdd = useEvent((selected: unknown): void => {
    const picked = Array.isArray(selected)
      ? (selected as IDropdownItem[])[0]
      : (selected as IDropdownItem | null);
    addCardFilter({ templateKey: picked?.value as string | undefined });
    setIsAddOpen(false);
  });

  const hasAnything = Boolean(
    Object.keys(schema).length || Object.keys(values).length,
  );

  if (!hasAnything) {
    return null;
  }

  return (
    <div className="card-filters">
      <div className="card-filters-label">
        {t("dashboard.dynamic.cardFilters")}
      </div>
      <div className="card-filters-row">
        <DashboardCardFilters
          schema={schema}
          values={values}
          singleValueFilterKeys={singleValueFilterKeys}
          onValuesChange={setCardFilterValues}
          onRemove={removeCardFilter}
        />
        <FilterDropdownWrapper
          isFilterItem={false}
          isOpen={isAddOpen}
          onClose={handleCloseAdd}
          target={
            <FramerTooltip content={t("general.ADD_NEW_FILTER")}>
              <IconButton
                data-e2e="dashboard-card-add-filter"
                onClick={handleToggleAdd}
                disabled={!addableOptions.length}
              >
                <PIC.PlusSvg />
              </IconButton>
            </FramerTooltip>
          }
        >
          <Select
            isMulti
            // eslint-disable-next-line jsx-a11y-x/no-autofocus -- intentional: focus the field when opened by user action
            autoFocus
            backspaceRemovesValue={false}
            components={{
              DropdownIndicator: null,
              IndicatorSeparator: null,
              Control: FilterSelectControl,
            }}
            controlShouldRenderValue={false}
            hideSelectedOptions={false}
            isClearable
            menuIsOpen
            options={addableOptions}
            onChange={onAdd}
            placeholder={`${t("general.SEARCH")}...`}
            styles={stylesComputed}
            tabSelectsValue={false}
            noOptionsMessage={() => t("errors.noOptionsMessage")}
          />
        </FilterDropdownWrapper>
      </div>
    </div>
  );
};
